import Bugsnag from '@bugsnag/js';
import posthog from 'posthog-js';

import { clientCookies, userCookies } from '@/shared/constants/cookies';
import { useConfigStore } from '@/shared/pinia/modules/config';
import { useUserStore } from '@/shared/pinia/modules/user';
import { clearCookie, getCookie, setCookie } from '@/shared/utils/cookie';
import { contextualError } from '@/shared/utils/error';
import { hashUserData } from '@/shared/utils/tracking';

const userId = () => {
  const loggedInDistinctId = getCookie(clientCookies.PRODUCT_ANALYTICS_USER_ID);
  const distinctId = getCookie(clientCookies.PRODUCT_ANALYTICS_CLIENT_ID);

  if (loggedInDistinctId) {
    return loggedInDistinctId;
  }

  if (distinctId) {
    return distinctId;
  }
};

const userProperties = () => {
  const userStore = useUserStore();
  const configStore = useConfigStore();

  const consentTrackedProperties = {
    country: configStore.countryCode,
    language: configStore.language
  };

  const alwaysTrackedProperties = {
    isLoggedIn: userStore.isLogged
  };

  if (!window.Shopify.customerPrivacy?.preferencesProcessingAllowed()) {
    return alwaysTrackedProperties;
  }

  return {
    ...alwaysTrackedProperties,
    ...consentTrackedProperties
  };
};

const refreshCookieStorage = () => {
  const userStore = useUserStore();

  if (userStore.customer?.uuid) {
    return setCookie(
      clientCookies.PRODUCT_ANALYTICS_USER_ID,
      hashUserData(userStore.customer.uuid)
    );
  }
  clearCookie(clientCookies.PRODUCT_ANALYTICS_USER_ID);

  const clientId = getCookie(clientCookies.PRODUCT_ANALYTICS_CLIENT_ID);
  if (!clientId) {
    setCookie(clientCookies.PRODUCT_ANALYTICS_CLIENT_ID, crypto.randomUUID());
  }
};

const aliasUser = () => {
  const loggedInDistinctId = getCookie(clientCookies.PRODUCT_ANALYTICS_USER_ID);
  const distinctId = getCookie(clientCookies.PRODUCT_ANALYTICS_CLIENT_ID);

  if (loggedInDistinctId && distinctId) {
    posthog.alias(loggedInDistinctId, distinctId);
    clearCookie(clientCookies.PRODUCT_ANALYTICS_CLIENT_ID);
  }
};

const setFeatureFlagProperties = () => {
  posthog.onFeatureFlags(() => {
    if (posthog.get_property('A/A Test Variant') !== undefined) {
      return;
    }

    posthog.setPersonProperties({
      'A/A Test Variant': posthog.getFeatureFlag('a-a-test')
    });
  });
};

export const initPostHog = () => {
  if (!window.Hobbii?.flags.enableBetaProductAnalytics) {
    return;
  }

  setTimeout(() => {
    refreshCookieStorage();

    const distinctId = userId();

    if (!distinctId) {
      return;
    }

    posthog.init(import.meta.env.VITE_POSTHOG_API_KEY, {
      api_host: import.meta.env.VITE_POSTHOG_API_HOST,
      defaults: '2025-05-24',
      person_profiles: 'always',
      capture_pageview: true,
      capture_pageleave: false
    });

    if (posthog.get_distinct_id() !== distinctId) {
      posthog.identify(distinctId, userProperties());
      aliasUser();
      setFeatureFlagProperties();
    }

    window.posthog = posthog;

    try {
      const consentSettings = JSON.parse(
        getCookie(userCookies.CONSENT_STATUS) ?? 'null'
      );

      if (
        consentSettings &&
        consentSettings['PostHog.com'] &&
        posthog.has_opted_out_capturing()
      ) {
        posthog.opt_in_capturing();
      }

      if (
        (!consentSettings || !consentSettings['PostHog.com']) &&
        posthog.has_opted_in_capturing()
      ) {
        posthog.opt_out_capturing();
      }
    } catch (error) {
      if (posthog.has_opted_in_capturing()) {
        posthog.opt_out_capturing();
      }
      const errorWithContext = contextualError(
        'Parsing consent settings for Posthog',
        error
      );

      if (!errorWithContext) {
        return;
      }

      if (Bugsnag.isStarted()) {
        Bugsnag.notify(errorWithContext);
      }
    }
  }, 200);
};
